/* ============================================================
   Вход через Google и Telegram — общий блок для страниц входа
   (старая витрина src/pages/Auth.jsx и новая src/fed/pages/Auth.jsx).

   Кнопки рисуются по ответу GET /api/oauth/config: если ключей
   провайдера нет в окружении бэкенда, кнопка не появляется —
   лучше её отсутствие, чем кнопка, ведущая в ошибку.

   Google — обычный redirect-flow (уходим на бэкенд, он на Google).
   Telegram — официальный виджет: свой iframe со своей вёрсткой,
   стилизовать его нельзя, поэтому под ним просто своя рамка.
   ============================================================ */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store';

const TELEGRAM_WIDGET_SRC = 'https://telegram.org/js/telegram-widget.js?22';

export default function OAuthButtons({ next = null, dividerLabel = null, style }) {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const tgBox = useRef(null);

  useEffect(() => {
    let alive = true;
    useAppStore.getState().fetchOAuthConfig().then((c) => { if (alive) setCfg(c); });
    return () => { alive = false; };
  }, []);

  // Виджет Telegram вставляем скриптом: он сам заменит себя на iframe-кнопку.
  // Колбэк принимает подписанные данные и меняет их в сторе на JWT.
  useEffect(() => {
    if (!cfg?.telegram || !cfg.telegram_bot || !tgBox.current) return;
    window.onTelegramAuth = (tgUser) => {
      useAppStore.getState().telegramLogin(tgUser).catch(() => {});
    };
    const script = document.createElement('script');
    script.src = TELEGRAM_WIDGET_SRC;
    script.async = true;
    script.setAttribute('data-telegram-login', cfg.telegram_bot);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-userpic', 'false');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onTelegramAuth(user)');
    const box = tgBox.current;
    box.innerHTML = '';
    box.appendChild(script);
    return () => { box.innerHTML = ''; };
  }, [cfg]);

  if (!cfg || (!cfg.google && !cfg.telegram)) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }}>
      {/* разделитель живёт здесь же: без настроенных провайдеров блока нет
          целиком и на форме не остаётся висящей черты «или» */}
      {dividerLabel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' }}>
          <span style={{ flex: 1, height: 1, background: 'var(--line, rgba(255,255,255,.1))' }} />
          <span style={{ fontSize: 11, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--fg-faint, rgba(255,255,255,.4))' }}>
            {dividerLabel}
          </span>
          <span style={{ flex: 1, height: 1, background: 'var(--line, rgba(255,255,255,.1))' }} />
        </div>
      )}

      {cfg.google && (
        <button
          type="button"
          onClick={() => useAppStore.getState().startOAuthLogin('google', next)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            width: '100%', height: 50, cursor: 'pointer',
            background: '#fff', color: '#1f1f1f', border: '1px solid rgba(0,0,0,.12)',
            borderRadius: 10, fontSize: 15, fontWeight: 600,
            fontFamily: 'inherit', transition: 'filter .18s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.filter = 'brightness(0.94)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
        >
          <GoogleMark />
          {t('auth.googleLogin', 'Войти через Google')}
        </button>
      )}

      {cfg.telegram && cfg.telegram_bot && (
        <div
          ref={tgBox}
          style={{ display: 'flex', justifyContent: 'center', minHeight: 44, alignItems: 'center' }}
        />
      )}
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.9 2.4 30.5 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.2 17.7 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9.1h12.4c-.5 2.9-2.2 5.4-4.7 7l7.6 5.9c4.4-4.1 6.8-10.1 6.8-17.4z" />
      <path fill="#FBBC05" d="M10.5 28.7c-.5-1.4-.8-2.9-.8-4.7s.3-3.3.8-4.7l-7.9-6.1C1 16.3 0 20 0 24s1 7.7 2.6 10.8l7.9-6.1z" />
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.3-8.3 2.3-6.3 0-11.6-3.7-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
    </svg>
  );
}
